import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SovAILogo } from 'assets/images';
import { Menu, Xmark } from 'iconoir-react';
import { RouteNames } from 'utils/constants';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollToSection = (sectionId: string, offset = 80) => {
  const section = document.getElementById(sectionId);
  if (!section) {
    console.error('Section not found:', sectionId);
    return;
  }

  window.scrollTo({
    top: section.offsetTop - offset,
    // Honour the user's motion preference: jump instead of animating.
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });

  // Move keyboard focus to the target so the jump is not purely visual.
  section.setAttribute('tabindex', '-1');
  section.focus({ preventScroll: true });
};

// Shared focus treatment. Instant, no transition — a focus ring that animates
// in reads as lag. The offset colour now tracks the flat page ground rather
// than the retired purple header.
const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-ground rounded-sharp';

// Nav voice: monospace, small, lowercase, letter-spaced. Colour steps from
// muted (5.5:1 on the ground — AA for normal text) to full ink on hover, with
// no transition so the feedback lands on the same frame as the pointer.
const NAV_LINK =
  'px-3 py-2 no-underline font-mono text-[13px] lowercase tracking-[0.08em] ' +
  `text-muted hover:text-ink ${FOCUS_RING}`;

// Flat, square-cornered bordered control. Replaces the rounded purple pill.
const NAV_BUTTON =
  'inline-flex items-center justify-center px-4 py-[7px] border border-hairline-strong ' +
  'bg-transparent font-mono text-[13px] lowercase tracking-[0.08em] text-ink ' +
  `hover:border-accent hover:text-accent cursor-pointer rounded-sharp ${FOCUS_RING}`;

interface MenuListProps {
  name: string;
  /** In-app route — rendered as a router <Link>. */
  to?: string;
  /** External destination — rendered as an <a href>. */
  href?: string;
  /** Same-page scroll target or other side effect — rendered as a <button>. */
  action?: () => void;
}

const SideBarMenuList = [
  { name: 'Home', path: '/home' },
  { name: 'Profile', path: '/profile' },
  { name: 'Subscribe', path: '/subscribe' },
];

export default function AppBar() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const location = useLocation();
  // The catalogue lives at /datasets (and /datasets/:slug). It is a public,
  // marketing-facing surface, so it gets the main nav rather than the
  // signed-in account bar — previously it fell through to the account bar and
  // rendered with no navigation at all.
  const isPublicUrl =
    location.pathname === '/' ||
    location.pathname === '/faq' ||
    location.pathname === '/datasets' ||
    location.pathname.startsWith('/datasets/') ||
    location.pathname === '/about';

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const navigate = useNavigate();
  const handleLogin = async () => {
    await loginWithRedirect({
      appState: {
        returnTo: '/home',
      },
    });
  };

  const handleLogout = () => {
    window.location.href = '/logout';
  };

  // Scroll targets (`features`, `pricing`, `contact`) only exist on the landing
  // page. Now that /datasets also renders the main nav, those items have to
  // route home first instead of logging "Section not found".
  const goToSection = (sectionId: string) => {
    if (location.pathname === '/') {
      scrollToSection(sectionId);
      return;
    }
    navigate('/', { state: { scrollTo: sectionId } });
    // Let the landing page paint before looking for the anchor.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToSection(sectionId));
    });
  };

  const MenuList: MenuListProps[] = [
    // The catalogue is the primary destination on the site and was missing
    // from the nav entirely. It leads.
    { name: RouteNames.Datasets, to: '/datasets' },
    { name: 'Features', action: () => goToSection('features') },
    { name: 'Blog', href: 'https://blog.sov.ai' },
    { name: 'Pricing', action: () => goToSection('pricing') },
    { name: 'API Key', to: '/subscribe' },
    { name: 'Documentation', href: 'https://docs.sov.ai' },
    { name: 'FAQ', to: '/faq' },
    { name: 'About', to: '/about' },
    { name: 'Contact', action: () => goToSection('contact') },
  ];

  return (
    <>
      {isPublicUrl ? (
        // Flat near-black ground matching the page body, separated by a single
        // hairline. The purple gradient, and the 8-unit vertical padding that
        // made the bar read as a marketing banner, are both gone.
        <header className="w-full flex flex-row py-3 px-6 items-center justify-center bg-ground border-b border-hairline z-10">
          {/* The logo was an <img onClick> — not focusable, not activatable by
              keyboard, and invisible to assistive tech as a control. It is now
              a real router Link. */}
          <Link
            to="/"
            className={`shrink-0 ${FOCUS_RING}`}
            aria-label="SovAI home"
          >
            <img src={SovAILogo} alt="SovAI" className="object-cover w-32" />
          </Link>
          <nav
            aria-label="Main"
            className="flex flex-row items-center justify-center px-8 grow"
          >
            {/* Nav items were <div onClick>: unreachable by Tab and unannounced.
                In-app routes render as <Link>, external destinations as <a> with
                a real href, and scroll-to-section actions as <button>. */}
            {MenuList.map(item => {
              const className = `hidden lg:flex ${NAV_LINK}`;

              if (item.href) {
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    {item.name}
                  </a>
                );
              }

              if (item.to) {
                return (
                  <Link key={item.name} to={item.to} className={className}>
                    {item.name}
                  </Link>
                );
              }

              return (
                <button
                  key={item.name}
                  type="button"
                  className={`${className} bg-transparent border-0`}
                  onClick={item.action}
                >
                  {item.name}
                </button>
              );
            })}
          </nav>
          {/* The auth control was a rounded purple pill from the shared Button
              component. It is now a flat square-cornered bordered control in the
              same monospace voice as the nav. */}
          {isLoading ? (
            <span
              className={`hidden lg:inline-flex ${NAV_BUTTON} text-transparent border-hairline`}
              aria-hidden="true"
            >
              login
            </span>
          ) : isAuthenticated ? (
            <button
              type="button"
              className={`hidden lg:inline-flex ${NAV_BUTTON}`}
              onClick={() => {
                navigate('/home');
              }}
            >
              home
            </button>
          ) : (
            <button
              type="button"
              className={`hidden lg:inline-flex ${NAV_BUTTON}`}
              onClick={() => {
                handleLogin();
              }}
            >
              login
            </button>
          )}
          <button
            type="button"
            className={`lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center ${FOCUS_RING}`}
            aria-label="Open menu"
            aria-expanded={isDrawerOpen}
            onClick={toggleDrawer}
          >
            <Menu
              color="var(--text-ink)"
              width="1.5em"
              height="1.5em"
              aria-hidden="true"
            />
          </button>
          {isDrawerOpen && (
            <div className="fixed top-0 left-0 h-screen w-full bg-ground z-30 py-3 px-6 lg:hidden">
              <div className="flex flex-col h-full">
                <div className="flex flex-row items-center justify-between border-b border-hairline pb-3">
                  {isLoading ? (
                    <span
                      className={`${NAV_BUTTON} text-transparent`}
                      aria-hidden="true"
                    >
                      login
                    </span>
                  ) : isAuthenticated ? (
                    <button
                      type="button"
                      className={NAV_BUTTON}
                      onClick={() => {
                        navigate('/home');
                      }}
                    >
                      home
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={NAV_BUTTON}
                      onClick={() => {
                        handleLogin();
                      }}
                    >
                      login
                    </button>
                  )}
                  <button
                    type="button"
                    className={`min-w-[44px] min-h-[44px] flex items-center justify-center ${FOCUS_RING}`}
                    aria-label="Close menu"
                    onClick={toggleDrawer}
                  >
                    <Xmark
                      color="var(--text-ink)"
                      width="1.5em"
                      height="1.5em"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div className="flex flex-col">
                  <nav aria-label="Mobile" className="flex flex-col">
                    {/* Each row is a hairline-separated register entry rather
                        than an evenly-spaced 2xl marketing list. */}
                    {MenuList.map(item => {
                      const className =
                        'flex items-center min-h-[48px] no-underline border-b border-hairline ' +
                        'font-mono text-sm lowercase tracking-[0.08em] text-muted ' +
                        `hover:text-ink ${FOCUS_RING}`;

                      if (item.href) {
                        return (
                          <a
                            key={item.name}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={className}
                            onClick={toggleDrawer}
                          >
                            {item.name}
                          </a>
                        );
                      }

                      if (item.to) {
                        return (
                          <Link
                            key={item.name}
                            to={item.to}
                            className={className}
                            onClick={toggleDrawer}
                          >
                            {item.name}
                          </Link>
                        );
                      }

                      return (
                        <button
                          key={item.name}
                          type="button"
                          className={`${className} bg-transparent border-x-0 border-t-0 cursor-pointer text-left`}
                          onClick={() => {
                            item.action?.();
                            toggleDrawer();
                          }}
                        >
                          {item.name}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </header>
      ) : (
        <header className="w-full flex flex-row py-3 px-6 items-center justify-center relative bg-ground border-b border-hairline">
          <div className="flex flex-row grow lg:hidden">
            <button
              type="button"
              className={`min-w-[44px] min-h-[44px] flex items-center justify-center ${FOCUS_RING}`}
              aria-label="Open menu"
              aria-expanded={isDrawerOpen}
              onClick={toggleDrawer}
            >
              <Menu
                color="var(--text-ink)"
                width="1.5em"
                height="1.5em"
                aria-hidden="true"
              />
            </button>
          </div>
          <Link
            to="/"
            className={`absolute md:flex flex-row items-center justify-center lg:hidden ${FOCUS_RING}`}
            aria-label="SovAI home"
          >
            <img src={SovAILogo} alt="SovAI" className="object-cover w-32" />
          </Link>
          <Link
            to="/profile"
            className={`hidden md:flex flex-row items-center justify-end grow no-underline
                        font-mono text-[13px] lowercase tracking-[0.08em] text-muted
                        hover:text-ink ${FOCUS_RING}`}
          >
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-7 h-7 rounded-sharp border border-hairline object-cover"
              />
            ) : (
              <span className="w-7 h-7 rounded-sharp border border-hairline" />
            )}
            <span className="pl-3">
              {user?.given_name || 'Hello'} {user?.family_name || 'User'}
            </span>
          </Link>
          {isDrawerOpen && (
            <div className="fixed top-0 left-0 h-screen w-4/5 bg-ground border-r border-hairline z-30 py-3 px-6 lg:hidden">
              <div className="flex flex-col h-full">
                <div className="flex flex-row justify-start border-b border-hairline pb-3">
                  <button
                    type="button"
                    className={`min-w-[44px] min-h-[44px] flex items-center justify-center ${FOCUS_RING}`}
                    aria-label="Close menu"
                    onClick={toggleDrawer}
                  >
                    <Xmark
                      color="var(--text-ink)"
                      width="1.5em"
                      height="1.5em"
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <nav aria-label="Account" className="flex flex-col">
                  {SideBarMenuList.map(item => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`flex items-center min-h-[48px] no-underline border-b border-hairline
                                  font-mono text-sm lowercase tracking-[0.08em] text-muted
                                  hover:text-ink ${FOCUS_RING}`}
                      onClick={toggleDrawer}
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto w-full pb-6">
                  <button
                    type="button"
                    className={`w-full ${NAV_BUTTON}`}
                    onClick={handleLogout}
                  >
                    logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>
      )}
    </>
  );
}
